import { useState } from "react";
import { Button, ErrorMessage, Flex, LoadingIndicator, Select } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { AnswerProvenanceFilter } from "~/graphql/supergraphEnums";
import {
  useGetDdqResponseForProdOpsQuery,
  useGetDdqResponseProgressForProdOpsQuery,
  useGetDdqResponsesForProdOpsTaskQuery,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { AnswerStatusSummary } from "./AnswerStatusSummary";
import { SectionGroup } from "./SectionGroup";

const FILTER_LABELS: Record<AnswerProvenanceFilter, string> = {
  [AnswerProvenanceFilter.All]: "Entire DDQ",
  [AnswerProvenanceFilter.Generated]: "Generated by JunieAI",
  [AnswerProvenanceFilter.PreviouslyAnswered]: "Previously answered",
  [AnswerProvenanceFilter.NoResponse]: "No response",
};

export type AnswerQaViewProps = {
  arenaId: number;
  ddqProdOpsTaskId: string;
  canStartRun: boolean;
  hasEverRun: boolean;
  onRerunWhole: () => void;
  onRerunSection: (sectionId: string, sectionTitle: string) => void;
  // Undefined hides the button entirely rather than disabling it.
  onEditStructure: (() => void) | undefined;
};

export const AnswerQaView = ({
  arenaId,
  ddqProdOpsTaskId,
  canStartRun,
  hasEverRun,
  onRerunWhole,
  onRerunSection,
  onEditStructure,
}: AnswerQaViewProps) => {
  const [filter, setFilter] = useState<AnswerProvenanceFilter>(AnswerProvenanceFilter.All);
  // Null until the operator picks one explicitly — defaulting to the first response below
  // covers the common firm-wide / single-offering case without needing to sync state on load.
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);

  const { data: responsesData, error: responsesError } = useGetDdqResponsesForProdOpsTaskQuery({
    context: withClientContext("federation"),
    variables: { arenaId, ddqProdOpsTaskId },
  });
  const responses = responsesData?.ddqResponsesForProdOpsTask ?? [];
  const response = responses.find((item) => item.id === selectedResponseId) ?? responses[0];

  const { data: responseData, loading: isLoadingResponse } = useGetDdqResponseForProdOpsQuery({
    context: withClientContext("federation"),
    variables: { arenaId, id: response?.id ?? "", filter },
    skip: response == null,
  });

  const { data: progressData } = useGetDdqResponseProgressForProdOpsQuery({
    context: withClientContext("federation"),
    variables: { arenaId, id: response?.id ?? "" },
    skip: response == null,
  });

  const ddqResponse = responseData?.ddqResponseForProdOps;
  const progress = progressData?.ddqResponseProgressForProdOps;
  const topLevelSections = (ddqResponse?.sections ?? [])
    .filter((section) => section.parentSectionId == null)
    .sort((a, b) => a.position[a.position.length - 1] - b.position[b.position.length - 1]);

  // The whole-DDQ trigger has no dependency on a resolved response id (RunDdqGenerationInput
  // only needs arenaId + ddqProdOpsTaskId), so it stays available even before one exists —
  // only the filter/summary/tree below need actual response data to render.
  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} data-testid="ddq-answer-qa-view">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap={SPACING.SMALL} alignItems="center">
          {/* An offering-specific DDQ has one response per tagged offering. */}
          {responses.length > 1 && (
            <Select
              hideScrollBar
              label="Response"
              value={response?.id ?? ""}
              onChange={(value) => setSelectedResponseId(value as string)}
              data-testid="ddq-answer-qa-response-select"
            >
              <Select.Section>
                {responses.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.offering?.name ?? "Firm-wide"}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          )}
          {response != null && (
            <Select
              hideScrollBar
              label="Filter"
              value={filter}
              onChange={(value) => setFilter(value as AnswerProvenanceFilter)}
              data-testid="ddq-answer-qa-provenance-filter"
            >
              <Select.Section>
                {Object.values(AnswerProvenanceFilter).map((option) => (
                  <Select.Option key={option} value={option}>
                    {FILTER_LABELS[option]}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          )}
        </Flex>
        <Flex gap={SPACING.SMALL} alignItems="center">
          <Button
            disabled={!canStartRun}
            onClick={onRerunWhole}
            data-testid="ddq-rerun-generation"
          >
            {hasEverRun ? "Re-run AI job" : "Generate first draft"}
          </Button>
          {/* Hidden for now — onEditStructure is never passed truthy yet. */}
          {onEditStructure && (
            <Button
              variant="neutral"
              onClick={onEditStructure}
              data-testid="ddq-edit-structure-trigger"
            >
              Edit structure
            </Button>
          )}
        </Flex>
      </Flex>

      {responsesError && (
        <ErrorMessage data-testid="ddq-answer-qa-responses-error">
          {responsesError.message}
        </ErrorMessage>
      )}

      {response != null && (
        <>
          {progress && (
            <AnswerStatusSummary
              bankReusedQuestions={progress.bankReusedQuestions}
              generatedQuestions={progress.generatedQuestions}
              noResponseQuestions={progress.noResponseQuestions}
            />
          )}

          {isLoadingResponse ? (
            <LoadingIndicator data-testid="ddq-answer-qa-loading" />
          ) : (
            <Flex flexDirection="column" gap={SPACING.LARGE}>
              {topLevelSections.map((section) => (
                <SectionGroup
                  key={section.id}
                  arenaId={arenaId}
                  responseId={response.id}
                  section={section}
                  allSections={ddqResponse?.sections ?? []}
                  allQuestions={ddqResponse?.questions ?? []}
                  depth={0}
                  canRerun={canStartRun}
                  onRerunSection={onRerunSection}
                />
              ))}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
